import VueCookies from 'vue-cookies';
import { createRouter, createWebHistory } from 'vue-router';
import NotFound from './views/404.vue';
import ServerDownScreen from './views/500.vue';
import { clearAuthData, ensureValidAuthSession, TOKEN_KEY } from './utilsAuth';
import { hasFeature, hasRole } from "@/utils/permissions";
import { getSupplier } from "@/services/supplier-api";
import { getDefaultRoute } from "@/utils/default-route";
import { Capacitor } from '@capacitor/core';

const Login = () => import(/* webpackChunkName: "Login" */ './views/auth/loginScreen.vue');
const DefaultPage = () => import(/* webpackChunkName: "DefaultPage" */ './views/DefaultPage.vue');
const Dashboard = () => import(/* webpackChunkName: "Dashboard" */ './views/dashboard/DashboardScreen.vue');
const Orders = () => import(/* webpackChunkName: "Orders" */ './views/order/ordersScreen.vue');
const WorkTrackingSheets = () => import(/* webpackChunkName: "WorkTrackingSheets" */ './views/WorkTracking/WorkTrackingSheetsScreen.vue');
const NewOrder = () => import(/* webpackChunkName: "NewOrder" */ './views/order/addOrderScreen.vue');
const Invoices = () => import(/* webpackChunkName: "Invoices" */ './views/invoice/invoiceScreen.vue');
const NewInvoice = () => import(/* webpackChunkName: "NewInvoice" */ './views/invoice/addInvoiceScreen.vue');
const CreditNotes = () => import(/* webpackChunkName: "CreditNotes" */ './views/creditNote/CreditNoteScreen.vue');
const AddCreditNote = () => import(/* webpackChunkName: "AddCreditNote" */ './views/creditNote/AddCreditNoteScreen.vue');
const CreditNoteDetail = () => import(/* webpackChunkName: "CreditNoteDetail" */ './views/creditNote/CreditNoteDetailScreen.vue');
const ImportInvoices = () => import(/* webpackChunkName: "ImportInvoices" */ './views/invoice/ImportInvoicesScreen.vue');
const PurchaseInvoices = () => import(/* webpackChunkName: "PurchaseInvoices" */ './views/purchaseInvoice/PurchaseInvoiceScreen.vue');
const NewPurchaseInvoice = () => import(/* webpackChunkName: "NewPurchaseInvoice" */ './views/purchaseInvoice/addPurchaseInvoiceScreen.vue');
const AddWorkTrackingSheets = () => import(/* webpackChunkName: "AddWorkTrackingSheets" */ './views/WorkTracking/addWorkTrackingSheetsScreen.vue');
const About = () => import(/* webpackChunkName: "About" */ './views/settings/aboutScreen.vue');
const Banner = () => import(/* webpackChunkName: "Banner" */ './views/settings/bannerScreen.vue');
const News = () => import(/* webpackChunkName: "News" */ './views/settings/newsLetterScreen.vue');
const Events = () => import(/* webpackChunkName: "Events" */ './views/settings/eventScreen.vue');
const Gallery = () => import(/* webpackChunkName: "Gallery" */ './views/settings/galleryScreen.vue');
const Teams = () => import(/* webpackChunkName: "Teams" */ './views/settings/teamsScreen.vue');
const SmtpSettings = () => import(/* webpackChunkName: "SmtpSettings" */ './views/settings/smtpScreen.vue');
const ElfatooraSettings = () => import(/* webpackChunkName: "ElfatooraSettings" */ './views/settings/elfatooraScreen.vue');
const DocumentsSettings = () => import(/* webpackChunkName: "DocumentsSettings" */ './views/settings/documentsScreen.vue');
const CustomerScreen = () => import(/* webpackChunkName: "CustomerScreen" */ './views/users/customerScreen.vue');
const PartnerScreen = () => import(/* webpackChunkName: "PartnerScreen" */ './views/users/partnerScreen.vue');
const SalesScreen = () => import(/* webpackChunkName: "SalesScreen" */ './views/users/salesScreen.vue');
const AccessHistoryScreen = () => import(/* webpackChunkName: "AccessHistoryScreen" */ './views/accessHistory/accessHistoryScreen.vue');
const AssistantAnalyticsScreen = () => import(/* webpackChunkName: "AssistantAnalyticsScreen" */ './views/assistant/AssistantAnalyticsScreen.vue');
const InvoiceProfileScreen = () => import(/* webpackChunkName: "InvoiceProfileScreen" */ './views/profile/SupplierProfile.vue');
const EmployeeScreen = () => import(/* webpackChunkName: "EmployeeScreen" */ './views/employees/EmployeeScreen.vue');
const SalaryAdvanceEmployeesScreen = () => import(/* webpackChunkName: "SalaryAdvanceEmployeesScreen" */ './views/employees/SalaryAdvanceEmployeesScreen.vue');
const EmployeeCreditScreen = () => import(/* webpackChunkName: "EmployeeCreditScreen" */ './views/employees/EmployeeCreditScreen.vue');
const EmployeeContractBuilder = () => import(/* webpackChunkName: "EmployeeContractBuilder" */ './views/employees/contracts/EmployeeContractBuilderModal.vue');
const PayrollScreen = () => import(/* webpackChunkName: "PayrollScreen" */ './views/payroll/PayrollScreen.vue');
const ExpenseScreen = () => import(/* webpackChunkName: "ExpenseScreen" */ './views/expenses/ExpenseScreen.vue');
const AlertScreen = () => import(/* webpackChunkName: "AlertScreen" */ './views/agenda/AlertScreen.vue');
const PieceCounterScreen = () => import(/* webpackChunkName: "PieceCounterScreen" */ './views/counterPiece/PieceCounterScreen.vue');
const PurchasingSuppliersScreen = () => import(/* webpackChunkName: "PurchasingSuppliersScreen" */ './views/users/PurchasingSuppliers.vue');
const PaymentStatusScreen = () => import(/* webpackChunkName: "PaymentStatusScreen" */ './views/dashboard/PaymentStatusScreen.vue');
const CatalogScreen = () => import(/* webpackChunkName: "CatalogScreen" */ './views/catalog/CatalogScreen.vue');
const CatalogCategoryScreen = () => import(/* webpackChunkName: "CatalogCategoryScreen" */ './views/catalog/CatalogCategoryScreen.vue');
const CatalogSubcategoryScreen = () => import(/* webpackChunkName: "CatalogSubcategoryScreen" */ './views/catalog/CatalogSubcategoryScreen.vue');
const WarehouseScreen = () => import(/* webpackChunkName: "WarehouseScreen" */ './views/stock/WarehouseScreen.vue');
const StockStatusScreen = () => import(/* webpackChunkName: "StockStatusScreen" */ './views/stock/StockStatusScreen.vue');
const InventoryScreen = () => import(/* webpackChunkName: "InventoryScreen" */ './views/stock/InventoryScreen.vue');
const LandingPage = () => import(/* webpackChunkName: "LandingPage" */ './views/public/LandingPage.vue');
const ReportScreen = () => import(/* webpackChunkName: "ReportScreen" */ './views/reports/ReportScreen.vue');
const ReportEditorScreen = () => import(/* webpackChunkName: "ReportEditorScreen" */ './views/reports/ReportEditorScreen.vue');

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
        path: '/',
        name: 'home',
        component: LandingPage
    },

    {
        path: '/default',
        name: 'DefaultPage',
        component: DefaultPage,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'admin' }
    },

    {
        path: '/dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: { requiresAuth: true, feature: 'DASHBOARD', requiredRole: 'supplier' }
    },
        {
        path: '/document/fiche-suivi',
        name: 'work_tracking_sheet',
        component: WorkTrackingSheets,
         meta: { requiresAuth: true, feature: 'WORK_TRACKING_SHEET', requiredRole: 'supplier' }
    },
        {
          path: '/document/generate/fiche-suivi/:specialRef',
          name: 'generate-work-tracking-sheet',
          component: AddWorkTrackingSheets,
          meta: { requiresAuth: true, feature: 'WORK_TRACKING_SHEET', requiredRole: 'supplier' }
        },
    {
        path: '/document/quote',
        name: 'quote',
        component: Orders,
         meta: { requiresAuth: true, feature: 'ORDER', requiredRole: 'supplier' }
    },
    {
        path: '/document/invoice',
        name: 'invoice',
        component: Invoices,
         meta: { requiresAuth: true, feature: 'ORDER', requiredRole: 'accountant' }
    },
    {
        path: '/document/invoice/import',
        name: 'import-invoices',
        component: ImportInvoices,
         meta: { requiresAuth: true, feature: 'ORDER', requiredRole: 'supplier' }
    },
    {
        path: '/document/generate/invoice/:specialRef',
        name: 'generate-invoice',
        component: NewInvoice,
        meta: { requiresAuth: true, feature: 'ORDER', requiredRole: 'accountant' }
    },
    {
        path: '/document/credit-note',
        name: 'credit-notes',
        component: CreditNotes,
        meta: { requiresAuth: true, feature: 'ORDER', requiredRole: 'accountant' }
    },
    {
        path: '/document/credit-note/add',
        name: 'add-credit-note',
        component: AddCreditNote,
        meta: { requiresAuth: true, feature: 'ORDER', requiredRole: 'accountant' }
    },
    {
        path: '/document/credit-note/:id',
        name: 'credit-note-detail',
        component: CreditNoteDetail,
        meta: { requiresAuth: true, feature: 'ORDER', requiredRole: 'accountant' }
    },
    {
        path: '/document/purchase/invoice',
        name: 'purchase invoice',
        component: PurchaseInvoices,
         meta: { requiresAuth: true, feature: 'ORDER', requiredRole: 'supplier', module: 'purchaseEnabled' }
    },
    {
        path: '/document/generate/purchase/invoice/:specialRef',
        name: 'generate purchase invoice',
        component: NewPurchaseInvoice,
        meta: { requiresAuth: true, feature: 'ORDER', requiredRole: 'supplier', module: 'purchaseEnabled' }
    },

    {
        path: '/document/generate/quote/:specialRef',
        name: 'generate-quote',
        component: NewOrder,
        meta: { requiresAuth: true, feature: 'ORDER', requiredRole: 'supplier' }
    },

    {
        path: '/settings/about',
        name: 'about',
        component: About,
         meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'admin' }
    },
    {
        path: '/settings/banner',
        name: 'banner',
        component: Banner,
         meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'admin' }
    },
    {
        path: '/settings/news',
        name: 'news',
        component: News,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'admin' }
    },
    {
        path: '/settings/events',
        name: 'events',
        component: Events,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'admin' }
    },
    {
        path: '/settings/gallery',
        name: 'gallery',
        component: Gallery,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'admin' }
    },
    {
        path: '/settings/teams',
        name: 'teams',
        component: Teams,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'admin' }
    },
    {
      path: '/configuration/smtp',
      name: 'smtpConfiguration',
      component: SmtpSettings,
      meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'notification', module: 'notificationEnabled' }
    },
    {
      path: '/configuration/documents',
      name: 'documentsConfiguration',
      component: DocumentsSettings,
      meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier' }
    },
    {
      path: '/configuration/elfatoora',
      name: 'elfatooraConfiguration',
      component: ElfatooraSettings,
      meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier' }
    },
    {
        path: '/users/sales',
        name: 'usersSales',
        component: SalesScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'admin' }
    },
    {
        path: '/users/access-history',
        name: 'accessHistory',
        component: AccessHistoryScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'admin' }
    },
    {
        path: '/admin/assistant-analytics',
        name: 'assistantAnalytics',
        component: AssistantAnalyticsScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'admin' }
    },
    {
        path: '/users/partner',
        name: 'usersPartner',
        component: PartnerScreen,
        meta: { requiresAuth: true, feature: 'ECOMMERCE', requiredRole: 'admin' }
    },
    {
        path: '/users/customer',
        name: 'customer',
        component: CustomerScreen,
        meta: { requiresAuth: true, feature: 'ECOMMERCE', requiredRole: 'supplier' }
    },

    {
        path: '/users/purchasing-suppliers',
        name: 'purchasingSuppliers',
        component: PurchasingSuppliersScreen,
        meta: { requiresAuth: true, feature: 'ECOMMERCE', requiredRole: 'supplier', module: 'purchaseEnabled' }
    },

    {
        path: '/employees',
        name: 'employees',
        component: EmployeeScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier', module: 'employeeEnabled' }
    },
    {
        path: '/salary-advances',
        name: 'salaryAdvances',
        component: SalaryAdvanceEmployeesScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier', module: 'employeeEnabled' }
    },
    {
        path: '/payroll',
        name: 'payroll',
        component: PayrollScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier', module: 'employeeEnabled' }
    },
    {
        path: '/employees/:employeeId/credits',
        name: 'employeeCredits',
        component: EmployeeCreditScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier', module: 'employeeEnabled' }
    },
    {
        path: '/employees/:employeeId/contract-builder',
        name: 'employeeContractBuilder',
        component: EmployeeContractBuilder,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier', module: 'employeeEnabled' }
    },

    {
        path: '/expenses',
        name: 'expenses',
        component: ExpenseScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier', module: 'expenseEnabled' }
    },
    {
        path: '/reports',
        name: 'reports',
        component: ReportScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier', module: 'reportEnabled' }
    },
    {
        path: '/reports/new',
        name: 'new-report',
        component: ReportEditorScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier', module: 'reportEnabled' }
    },
    {
        path: '/reports/:id',
        name: 'edit-report',
        component: ReportEditorScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier', module: 'reportEnabled' }
    },

    {
        path: '/catalog',
        name: 'catalog',
        component: CatalogScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier' }
    },
    {
        path: '/catalog/categories',
        name: 'catalog-categories',
        component: CatalogCategoryScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier' }
    },
    {
        path: '/catalog/subcategories',
        name: 'catalog-subcategories',
        component: CatalogSubcategoryScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier' }
    },
    {
        path: '/stock/status',
        name: 'stock-status',
        component: StockStatusScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier' }
    },
    {
        path: '/stock/warehouses',
        name: 'stock-warehouses',
        component: WarehouseScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier' }
    },
    {
        path: '/stock/inventories',
        name: 'stock-inventories',
        component: InventoryScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier' }
    },

    {
        path: '/agenda/alerts',
        name: 'agendaAlerts',
        component: AlertScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'notification', module: 'notificationEnabled' }
    },

    {
        path: '/invoice-profile/',
        name: 'invoiceProfile',
        component: InvoiceProfileScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier' }
    },

    {
        path: '/payment-status',
        name: 'payment status',
        component: PaymentStatusScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier' }
    },


    {
        path: '/piece-counter',
        name: 'counter piece',
        component: PieceCounterScreen,
        meta: { requiresAuth: true, feature: 'DEFAULT', requiredRole: 'supplier' }
    },

    { path: '/login', component: Login },
    
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      meta: { requiresAuth: true },
      component: NotFound
    },

    {
      path: '/error-500',
      name: 'ServerDownScreen',
      meta: { requiresAuth: true },
      component: ServerDownScreen
    }
    
      ],


  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    return { left: 0, top: 0 };
  }
});

router.beforeEach(async (to, from, next) => {
  if (to.path === '/logout') {
    await clearAuthData();
    next('/login');
    return;
  }

  const isNativeApp = Capacitor.isNativePlatform();
  const accessToken = isNativeApp
    ? await ensureValidAuthSession()
    : VueCookies.get(TOKEN_KEY);

  if (isNativeApp && to.path === '/') {
    next(accessToken ? getDefaultRoute() : '/login');
    return;
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!accessToken) {
      if (to.path !== '/login') {
        next('/login');
        return;
      }
    } else {
      const defaultRoute = getDefaultRoute();
      const redirectToDefault = () => next(to.path === defaultRoute ? undefined : defaultRoute);

      if (to.path === '/login') {
        next(defaultRoute);
        return;
      }

      const feature = to.meta.feature;
      if (feature && !hasFeature(feature)) {
        redirectToDefault();
        return;
      }

      const requiredRole = to.meta.requiredRole;
      if (requiredRole && !hasRole(requiredRole)) {
        redirectToDefault();
        return;
      }

      const requiredModule = to.meta.module;
      if (requiredModule) {
        try {
          const supplier = await getSupplier();
          if (!supplier?.[requiredModule]) {
            redirectToDefault();
            return;
          }
        } catch (error) {
          redirectToDefault();
          return;
        }
      }
    }
  } else {
    if (accessToken && to.path === '/login') {
      next(getDefaultRoute());
      return;
    }
  }

  next();
});

export default router;
